import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Alert, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Timeline, type TimelineItem } from '@shared/components/Timeline';
import { EmptyState } from '@shared/components/EmptyState';
import { auditApi } from '@api/audit';

interface Props {
  articleId: string;
  open?: boolean;
}

// Maps a raw audit action code to its i18n key under `activity.actions.*`.
const ACTION_LABEL_KEY: Record<string, string> = {
  'article.create': 'create',
  'article.update': 'update',
  'article.publish': 'publish',
  'article.archive': 'archive',
  'article.restore': 'restore',
  'article.review.submit': 'reviewSubmit',
  'article.review.approve': 'reviewApprove',
  'article.review.reject': 'reviewReject',
  'article.featured.toggle': 'featuredToggle',
  'article.delete': 'delete',
  'article.version.restore': 'versionRestore',
};

function toneFor(action: string): TimelineItem['tone'] {
  if (action.includes('publish') || action.includes('approve')) return 'success';
  if (action.includes('reject') || action.includes('delete')) return 'danger';
  if (action.includes('archive')) return 'warning';
  if (action.includes('review.submit') || action.includes('version'))
    return 'info';
  return 'default';
}

/**
 * Per-article activity timeline.
 *
 * S9 note: the BE does not yet expose an article-scoped activity endpoint.
 * We surface the locally-recorded audit log entries when available, otherwise
 * an informational empty-state.
 */
export function ArticleActivityPanel({ articleId, open = true }: Props) {
  const { t } = useTranslation(['articles', 'common', 'validation', 'errors']);
  const td = t as unknown as (key: string) => string;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['article-activity', articleId],
    queryFn: () => auditApi.list({ resource: 'article', pageSize: 50 }),
    enabled: open,
    retry: false,
  });

  const items = useMemo<TimelineItem[]>(() => {
    const list = data?.items ?? [];
    return list
      .filter((e) => e.resourceId === articleId)
      .slice(0, 20)
      .map((entry) => ({
        id: entry.id,
        time: dayjs(entry.at).format('DD.MM.YYYY HH:mm'),
        actor: entry.actorName ?? t('activity.unknownActor'),
        action: ACTION_LABEL_KEY[entry.action]
          ? td(`activity.actions.${ACTION_LABEL_KEY[entry.action]}`)
          : entry.action,
        detail: entry.summary,
        tone: toneFor(entry.action),
      }));
  }, [data, articleId, t, td]);

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="info"
        showIcon
        message={t('activity.errorMessage')}
        description={t('activity.errorDescription')}
      />
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        title={t('activity.emptyTitle')}
        description={t('activity.emptyDescription')}
      />
    );
  }

  return <Timeline items={items} />;
}
